import { getFilePreSignedUrl } from "../../../utils/api";
import dayjs from "dayjs";
import { UI_DATE_FORMAT } from "../../../consts";

export function buildForecastFilePath(
  prefix,
  siteName,
  greenhouse,
  plot,
  isoDate,
) {
  if (prefix) {
    return `${prefix}/${siteName}/${greenhouse}/${plot}/forecasts/${isoDate}/forecast.json`;
  }
  return `${siteName}/${greenhouse}/${plot}/forecasts/${isoDate}/forecast.json`;
}

export function getTotalYield(
  jsonData,
  avgYieldKeyName,
  totalRows,
  areaPerRow,
  areaOfGH,
  averageYieldPerSqMeterKeyName,
) {
  const rawValue = jsonData?.[avgYieldKeyName] ?? 0;
  if (areaPerRow && areaOfGH) {
    return Math.round((rawValue / (areaPerRow ?? 1)) * (areaOfGH ?? 0));
  }
  if (averageYieldPerSqMeterKeyName && areaOfGH) {
    const yieldPerSqMeter = jsonData?.[averageYieldPerSqMeterKeyName] ?? 0;
    return Math.round(yieldPerSqMeter * (areaOfGH ?? 0));
  }
  return Math.round(rawValue * (totalRows ?? 0));
}

export function weekToNumber(year, week) {
  return year * 52 + week;
}

export function isWeekInRange(year, week, from, to) {
  const val = weekToNumber(year, week);
  const fromVal = weekToNumber(from.YEAR, from.WEEK);
  const toVal = weekToNumber(to.YEAR, to.WEEK);
  return val >= fromVal && val <= toVal;
}

export async function fetchOneForecastJSON({
  orgId,
  siteId,
  path,
  tempCache,
  fetchDataCache,
  setFetchDataCache,
}) {
  if (fetchDataCache[path]) {
    tempCache[path] = fetchDataCache[path];
    return fetchDataCache[path];
  }
  try {
    const preSignedResponse = await getFilePreSignedUrl(
      orgId,
      siteId,
      path,
      "azure",
      true,
      true,
    );
    const preSignedUrl = preSignedResponse?.data?.data?.url;
    if (!preSignedUrl) {
      tempCache[path] = null;
      setFetchDataCache((prev) => ({ ...prev, [path]: null }));
      return null;
    }
    const resp = await fetch(preSignedUrl);
    if (!resp.ok) {
      tempCache[path] = null;
      setFetchDataCache((prev) => ({ ...prev, [path]: null }));
      return null;
    }
    const jsonData = await resp.json();
    tempCache[path] = jsonData;
    setFetchDataCache((prev) => ({ ...prev, [path]: jsonData }));
    return jsonData;
  } catch (err) {
    console.error("Error fetching forecast:", err);
    tempCache[path] = null;
    setFetchDataCache((prev) => ({ ...prev, [path]: null }));
    return null;
  }
}

export function getSiteForecastConfig(siteObj, cropName) {
  const cropMeta = siteObj?.metadata?.[cropName] || {};
  const forecastMeta = cropMeta?.forecast || {};

  const averageYieldKeyName =
    forecastMeta.AVERAGE_YIELD_KEY || "AverageRowYield";

  const refinedAverageYieldKeyName =
    forecastMeta.REFINED_AVERAGE_YIELD_KEY || "RefinedAverageRowYield";

  let summaryAverageYieldKeyName = forecastMeta.SUMMARY_AVERAGE_YIELD_KEY;
  if (!summaryAverageYieldKeyName) {
    summaryAverageYieldKeyName = refinedAverageYieldKeyName;
  }

  return {
    prefixForecastPath: forecastMeta.PREFIX_FORECAST_PATH || "",
    totalRows: forecastMeta.TOTAL_ROWS || 0,
    areaPerRow: forecastMeta.AREA_PER_ROW || 0,
    areaOfGH: forecastMeta.AREA_OF_GH || 0,
    averageYieldKeyName,
    refinedAverageYieldKeyName,
    summaryAverageYieldKeyName,
    averageYieldPerSqMeterKeyName:
      forecastMeta.AVERAGE_YIELD_PER_SQ_METER || "AverageRowYieldPerSqMeter",
  };
}


export function filterEmptyDates(lines) {
  const sumMap = {};
  for (const line of lines) {
    for (const point of line.data) {
      const dateStr = point.x;
      const val = point.y || 0;
      sumMap[dateStr] = (sumMap[dateStr] || 0) + val;
    }
  }

  const datesToRemove = new Set(
    Object.entries(sumMap)
      .filter(([_, sumVal]) => sumVal === 0)
      .map(([dateStr]) => dateStr),
  );

  return lines.map((line) => {
    const filteredData = line.data.filter(
      (point) => !datesToRemove.has(point.x),
    );
    return {
      ...line,
      data: filteredData,
    };
  });
}

export function buildFilteredDateHeaders(filteredLines) {
  const dateSet = new Set();
  filteredLines.forEach((line) => {
    line.data.forEach((point) => {
      dateSet.add(point.x);
    });
  });

  const sortedDates = [...dateSet].sort((a, b) =>
    dayjs(a, UI_DATE_FORMAT).diff(dayjs(b, UI_DATE_FORMAT)),
  );
  return sortedDates.map((date) =>
    dayjs(date, UI_DATE_FORMAT).format("M/D/YYYY"),
  );
}

export const getResponsiveLoadingIconSize = () => {
  const width = window.innerWidth;
  if (width < 640) {
    return "100px";
  } else if (width < 1024) {
    return "150px";
  }
  return "200px";
};
